/**
 * Which org the console acts in (SPEC decision 22, plan P22/P23), sent on every API call:
 *   - `X-Hangar-Org`: the org a user who belongs to several picked in the switcher (kept in `localStorage`, so the
 *     next visit lands in the same org). Without it the API answers `GET /me` for the oldest membership and lists
 *     `orgs`; every other route needs it once there are several.
 *   - `X-Hangar-Preview-Org`: a platform operator's read-only look at a client (kept in `sessionStorage`: it ends with
 *     the tab). The API decides who may preview; this only remembers the choice.
 * Storage can be missing or throw (private windows, blocked site data): every access is guarded and the console then
 * simply forgets the choice.
 */
const ORG_KEY = 'hangar.org';
const PREVIEW_KEY = 'hangar.preview';

/** `suspended`: the client was suspended when the preview started (the banner says so; SPEC §17.6). */
export interface PreviewTarget { id: string; name: string; suspended?: boolean }

function read(storage: () => Storage, key: string): string | null {
  try { return storage().getItem(key); } catch { return null; }
}

function write(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch { /* no storage: the choice lasts until the page reloads at most */ }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;

export const selectedOrg = (): string | null => read(local, ORG_KEY);
export const selectOrg = (orgId: string | null): void => write(local, ORG_KEY, orgId);

export function previewTarget(): PreviewTarget | null {
  const raw = read(session, PREVIEW_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<PreviewTarget>;
    return typeof v.id === 'string' && typeof v.name === 'string' ? { id: v.id, name: v.name, ...(v.suspended === true ? { suspended: true } : {}) } : null;
  } catch { return null; }
}

export const setPreviewTarget = (target: PreviewTarget | null): void => write(session, PREVIEW_KEY, target ? JSON.stringify(target) : null);

/**
 * A user in several orgs who has not chosen yet: `GET /me` answered for its default org (the oldest membership), and
 * every other call needs that choice in `X-Hangar-Org`, so it is adopted. True when the calls made before it must run again.
 */
export function adoptDefaultOrg(me: { org: { id: string }; orgs?: unknown[] | undefined; preview?: boolean | undefined }): boolean {
  if (me.preview || previewTarget() || selectedOrg() || (me.orgs?.length ?? 0) < 2) return false;
  selectOrg(me.org.id);
  return true;
}

/** The org headers of the next request. */
export function orgHeaders(): Record<string, string> {
  const preview = previewTarget();
  if (preview) return { 'X-Hangar-Preview-Org': preview.id };
  const org = selectedOrg();
  return org ? { 'X-Hangar-Org': org } : {};
}

/**
 * Forgets a choice the API no longer honors (the user left that org, the operator role was revoked, the client was
 * removed), so the next request falls back to the default instead of failing forever. True when something changed.
 */
export function forgetRefusedChoice(status: number, message: string): boolean {
  // Not "The client preview is read-only": a refused write keeps the preview.
  const previewGone = (status === 403 && /client preview is reserved/i.test(message)) || (status === 404 && /to preview/i.test(message));
  if (previewTarget() && previewGone) { setPreviewTarget(null); return true; }
  if (selectedOrg() && status === 400 && /x-hangar-org/i.test(message)) { selectOrg(null); return true; }
  return false;
}
